import React, { type FC, useMemo, useCallback } from 'react';

import { useSessionData } from '@confluence/session-data';
import { useNativeCollabPresenceState } from '@confluence/native-collab';
import { isExternalShareRequest } from '@confluence/external-share-utils';

import type { CanvasCollaborator, CanvasCollaborators, Participant } from '../presenceTypes';
import { useParticipants } from '../presence-store';
import { PresenceAvatarGroup } from '../presence-avatar-group';
import { PresenceChangeboarding } from '../changeboarding/PresenceChangeboarding';

export type CanvasPresenceProps = {
	collaborators?: CanvasCollaborators | null;
	contentId: string;
	contentType: string;
	analyticSource?: string;
};

// don't have activity for canvas, just default to viewer
const ACTIVITY = 'viewer';
const CONTENT_MODE = 'view';

export const CanvasPresence: FC<CanvasPresenceProps> = ({
	collaborators,
	contentId,
	contentType,
	analyticSource,
}) => {
	const initialParticipants = useParticipants();
	const [{ presenceId }] = useNativeCollabPresenceState();
	const isExternal = isExternalShareRequest();
	const { userId } = useSessionData();

	const createOnClickHandler = (collaborator: CanvasCollaborator) => {
		return collaborator.followCursor === true
			? () => {
					collaborator.onClick?.();
				}
			: collaborator.onClick;
	};

	/**
	 * If we're not on an external route, get the current cached user; otherwise, wait for current user to come from canvas websocket
	 * Here's a more in-depth explanation
	 *
	 * isExternal = on a public link
	 * At this time external routes aren’t SSRd so we can’t preload. As a result, our user won't be cached
	 *
	 * The possible solutions are
	 * - Get external pages to be SSRd (which was too heavy of a lift at this time of implementation)
	 * - Add photos to the existing user call in SessionDataQuery or SiteInformationQuery
	 * - Just wait for collaborators to come in from websocket connection (which is the current implementation)
	 * right now, we have issues until we can preload users on external routes so don't worry about showing current user before socket connection establishes
	 * we could ad a preloader for external routes; however, external pages don't appeared to be SSRd.
	 *
	 * @param isExternal true if we're on an /external route, otherwise false
	 * @param collaborators list that comes from canvas websocket connection
	 * @returns current user from cache or canvas. Otherwise return undefined
	 */
	const getCurrentUser = useCallback(
		(collaborators?: CanvasCollaborators | null): Participant | undefined => {
			if (isExternal && collaborators?.me) {
				const { me } = collaborators;

				//userId from canvas will already be unidentified if user is anonymous and can't be null
				return {
					activity: ACTIVITY,
					color: me.color,
					followCursor: me.followCursor,
					id: me.userId,
					isCurrentUser: true,
					name: me.name,
					onClick: createOnClickHandler(me),
					src: me.avatar.src ?? '',
					userId: me.userId,
				};
			} else if (!isExternal) {
				const currentParticipantId = userId ?? presenceId;
				const currentParticipant = currentParticipantId
					? initialParticipants.get(currentParticipantId)
					: undefined;

				// always want to use the currentParticipant instead of waiting for "me" from collaborators for better performance
				if (currentParticipant) {
					return {
						activity: ACTIVITY,
						id: currentParticipant.id,
						isCurrentUser: true,
						isGuest: currentParticipant.isGuest,
						name: currentParticipant.name,
						src: currentParticipant.src,
						userId: currentParticipant.userId,
					};
				}
			}

			return undefined;
		},
		[isExternal, initialParticipants, presenceId, userId],
	);

	const participants = useMemo(() => {
		const result: Participant[] = [];

		const currentUser = getCurrentUser(collaborators);
		if (currentUser) {
			result.push(currentUser);
		}

		collaborators?.collaborators?.forEach((collaborator) => {
			const id = collaborator.userId;
			result.push({
				id,
				userId: id,
				name: collaborator.name,
				color: collaborator.color,
				src: collaborator.avatar.src ?? '',
				// we don't have activities on canvas, right now we need to default to viewer as downstream components depend on activity being present
				activity: ACTIVITY,
				onClick: createOnClickHandler(collaborator),
				followCursor: collaborator.followCursor,
			});
		});

		return result;
	}, [collaborators, getCurrentUser]);

	return (
		<PresenceChangeboarding
			contentId={contentId}
			contentMode={CONTENT_MODE}
			contentType={contentType}
			totalParticipants={participants.length}
		>
			<PresenceAvatarGroup
				contentId={contentId}
				contentMode={CONTENT_MODE}
				contentType={contentType}
				analyticSource={analyticSource}
				onLoadMoreClick={() => {}}
				participants={participants}
				totalParticipants={participants.length}
			/>
		</PresenceChangeboarding>
	);
};
